import { useState, useEffect } from "react";
import {
  Button,
  Flex,
  Grid,
  Modal,
  Notification,
  Text as MantineText,
  TextInput,
} from "@mantine/core";
import { IconMail, IconUser } from "@tabler/icons-react";
import "./PersonalInfoSetting.scss";
import { formatValue } from "@utils/helper";
import type { PersonalInfoSettings } from "@/types";
import { updatePersonalInfo } from "@utils/api";
import { showNotification } from "@mantine/notifications";
import AccountVerification from "@/pages/VerificationPage/components/AccountVerificationScreen";
import RolePackageBadges from "@/components/RolePackageBadges/RolePackageBadges";

interface PersonalInfoSettingProps {
  personalInfo: PersonalInfoSettings;
  setPersonalInfo: (personalInfo: PersonalInfoSettings) => void;
}

/**
 * This is a functional component for managing personal information settings.
 * It retrieves the personal information from local storage and allows the user to update their first name, last name, phone number, and email.
 * It also handles fetching business information and updating the personal information on the backend server.
 * The function also includes a form for users to input and save their personal information, with error handling for failed updates.
 *
 * @param {any} e - The event object for form submission
 * @return {void}
 */
const PersonalInfoSetting = ({
  personalInfo,
  setPersonalInfo,
}: PersonalInfoSettingProps) => {
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
    autoClose?: number;
  } | null>(null);
  const [initialValues, setInitialValues] =
    useState<PersonalInfoSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  useEffect(() => {
    setInitialValues(personalInfo);
  }, []);

  /**
   * checks for response status 200 and updates personal information
   * @param e event
   */
  const handleVerificationSuccess = () => {
    setShowVerificationModal(false);
    showNotification({
      title: "Success",
      message: "Email verified successfully!",
      color: "teal",
      autoClose: 8000,
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    if (!personalInfo?.phoneNumber) {
      showNotification({
        title: "Error",
        message: "Phone number is required",
        color: "red",
        autoClose: 8000,
      });
      setIsLoading(false);
      return;
    }

    try {
      // Create a payload object with only the changed values
      const payload: Partial<PersonalInfoSettings> = {};

      if (initialValues) {
        if (personalInfo.firstName !== initialValues.firstName) {
          payload.firstName = personalInfo.firstName;
        }
        if (personalInfo.lastName !== initialValues.lastName) {
          payload.lastName = personalInfo.lastName;
        }
        if (personalInfo.phoneNumber !== initialValues.phoneNumber) {
          payload.phoneNumber = personalInfo.phoneNumber;
        }
        if (personalInfo.email !== initialValues.email) {
          payload.email = personalInfo.email;
        }
      }

      // Only proceed if there are changes
      if (Object.keys(payload).length === 0) {
        showNotification({
          title: "No Changes",
          message: "No changes to update.",
          color: "blue",
        });
        return;
      }

      const personalInfoUpdate = await updatePersonalInfo(payload);

      if (personalInfoUpdate?.status === 200) {
        showNotification({
          title: "Success",
          message: "Personal info updated successfully!",
          color: "teal",
          autoClose: 8000,
        });
        const updatedPersonalInfo = {
          ...personalInfo,
          ...personalInfoUpdate.data.content.updatedData,
        };
        localStorage.setItem(
          "personalInfo",
          JSON.stringify(updatedPersonalInfo)
        );
        setPersonalInfo(updatedPersonalInfo);

        // Check if email verification is required
        if (personalInfoUpdate.data.content.fields?.includes("email")) {
          setShowVerificationModal(true);
        }
      }
    } catch (error) {
      console.error(error);
      showNotification({
        title: "Error",
        message: "Failed to update personal information.",
        color: "red",
        autoClose: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedPhoneNum = formatValue("phone", value);
    setPersonalInfo({ ...personalInfo, phoneNumber: formattedPhoneNum });
  };
  return (
    <div className="personal-form">
      {notification && (
        <Notification
          title={notification.title}
          color={notification.color}
          onClose={() => setNotification(null)}
          withCloseButton
        >
          {notification.message}
        </Notification>
      )}
      <br />
      <form onSubmit={handleSubmit} className="personal-form-container">
        <div className="personal-form-header">
          <Flex align="center" gap="sm">
            <h3 className="title">Personal Information</h3>
            <RolePackageBadges size="sm" gap="xs" />
          </Flex>
          <Button
            variant="outline"
            color="blue"
            type="submit"
            loading={isLoading}
            loaderProps={{ type: "dots", color: "white" }}
            disabled={isLoading}
            data-cy="savePersonalInfoButton"
          >
            Save
          </Button>
        </div>
        <Grid className="personal-form-grid">
          <Grid.Col>
            <TextInput
              leftSection={<IconUser color="#A3A9B0" />}
              placeholder={
                personalInfo.firstName ? personalInfo.firstName : "First Name"
              }
              label="First name"
              name="firstName"
              type="text"
              value={personalInfo.firstName}
              onChange={(e) =>
                setPersonalInfo({
                  ...personalInfo,
                  firstName: e.target.value,
                })
              }
              required={!!personalInfo.firstName}
              data-cy="firstNameInput"
            />
          </Grid.Col>
          <Grid.Col>
            <TextInput
              leftSection={<IconUser color="#A3A9B0" />}
              placeholder={
                personalInfo.lastName ? personalInfo.lastName : "Last Name"
              }
              label="Last name"
              name="lastName"
              type="text"
              value={personalInfo.lastName}
              onChange={(e) =>
                setPersonalInfo({
                  ...personalInfo,
                  lastName: e.target.value,
                })
              }
              required={!!personalInfo.lastName}
              data-cy="lastNameInput"
            />
          </Grid.Col>
          <Grid.Col>
            <TextInput
              leftSection={<IconMail color="#A3A9B0" />}
              placeholder={personalInfo?.email ? personalInfo?.email : "Email"}
              name="email"
              label="Email"
              type="text"
              value={personalInfo?.email}
              onChange={(e) =>
                setPersonalInfo({
                  ...personalInfo,
                  email: e.target.value,
                })
              }
              required={!!personalInfo?.email}
              data-cy="emailInput"
            />

            <MantineText
              size="sm"
              c="dimmed"
              style={{ marginTop: "0.5rem", maxWidth: "35rem" }}
            >
              Changing your email will require you to verify your new email
              address.
            </MantineText>
          </Grid.Col>
          <Grid.Col>
            <TextInput
              leftSection={<IconUser color="#A3A9B0" />}
              placeholder="Phone Number"
              label="Phone number"
              name="phoneNumber"
              type="text"
              pattern="^\d{3}-\d{3}-\d{4}$"
              value={personalInfo.phoneNumber}
              onChange={handlePhoneNumChange}
              required={!!personalInfo.phoneNumber}
              data-cy="phoneNumberInput"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}></Grid.Col>
        </Grid>
      </form>

      <Modal
        opened={showVerificationModal}
        onClose={() => {}} // Empty function to prevent closing
        size="100%"
        fullScreen
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <AccountVerification onComplete={handleVerificationSuccess} />
      </Modal>
    </div>
  );
};

export default PersonalInfoSetting;
